import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, IconButton, Button, CircularProgress, Alert, Tooltip, Snackbar,
  TextField, InputAdornment, Chip, MenuItem, Select, FormControl, InputLabel,
  Pagination, Tabs, Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import ScienceIcon from "@mui/icons-material/Science";
import ImageIcon from "@mui/icons-material/Image";
import TuneIcon from "@mui/icons-material/Tune";
import LanguageIcon from "@mui/icons-material/Language";
import { StyledDialog, inputStyles } from "./common";

const API_BASE = "/api";
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_KEY = "catalogo_estudios_page_size";

// Resalta substring (case + accent insensitive). Devuelve array de fragments.
function highlightMatch(text, term) {
  if (!term || !text) return text || "";
  const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const t = norm(term);
  const original = String(text);
  const normOriginal = norm(original);
  const parts = [];
  let i = 0;
  while (i < normOriginal.length) {
    const idx = normOriginal.indexOf(t, i);
    if (idx === -1) {
      parts.push({ text: original.slice(i), match: false });
      break;
    }
    if (idx > i) parts.push({ text: original.slice(i, idx), match: false });
    parts.push({ text: original.slice(idx, idx + t.length), match: true });
    i = idx + t.length;
  }
  return parts;
}

function HighlightText({ text, term }) {
  if (!term) return <>{text || ""}</>;
  const parts = highlightMatch(text, term);
  if (typeof parts === "string") return <>{parts}</>;
  return (
    <>
      {parts.map((p, i) =>
        p.match ? (
          <mark key={i} style={{ backgroundColor: "#fef08a", padding: "0 1px", borderRadius: 2 }}>
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

const CATEGORIA_META = {
  laboratorio: { label: "Laboratorio", color: "#0ea5e9", icon: <ScienceIcon sx={{ fontSize: 14 }} /> },
  imagen: { label: "Imagen", color: "#8b5cf6", icon: <ImageIcon sx={{ fontSize: 14 }} /> },
  especial: { label: "Especial", color: "#f59e0b", icon: <TuneIcon sx={{ fontSize: 14 }} /> },
};

function authHeaders() {
  const userId = localStorage.getItem("user_id");
  const h = { "Content-Type": "application/json" };
  if (userId) h["X-User-Id"] = String(userId);
  return h;
}

export default function AdminCatalogoEstudios() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categorias, setCategorias] = useState([]);
  const [counts, setCounts] = useState({ _total: 0, laboratorio: 0, imagen: 0, especial: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);

  // Filtros
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filtroCat, setFiltroCat] = useState("");
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const stored = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || "", 10);
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
  });

  // Dialog crear/editar
  const [dialog, setDialog] = useState(null); // {modo: 'crear'|'editar', data}
  // Dialog nombres locales por país
  const [nombresDialog, setNombresDialog] = useState(null); // { estudio }

  // Debounce buscador
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Reset page al cambiar filtros
  useEffect(() => { setPage(1); }, [debouncedQ, filtroCat, mostrarInactivos, pageSize]);

  // Persistir pageSize
  useEffect(() => {
    try { localStorage.setItem(PAGE_SIZE_KEY, String(pageSize)); } catch {}
  }, [pageSize]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, filtroCat, mostrarInactivos, page, pageSize]);

  async function cargar() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("q", debouncedQ);
      if (filtroCat) params.set("categoria", filtroCat);
      if (!mostrarInactivos) params.set("activo", "true");
      params.set("limit", String(pageSize));
      params.set("offset", String((page - 1) * pageSize));

      const r = await fetch(`${API_BASE}/admin/catalogo-estudios?${params}`, { headers: authHeaders() });
      if (!r.ok) {
        if (r.status === 403) throw new Error("Solo superadmins pueden gestionar el catálogo");
        throw new Error(`Error ${r.status}`);
      }
      const data = await r.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setCategorias(data.categorias || []);
      setCounts(data.counts_by_category || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  async function eliminar(item, hard = false) {
    if (!window.confirm(hard
      ? `¿Eliminar PERMANENTEMENTE "${item.nombre_estandar}"?\nSi tiene referencias, fallará.`
      : `¿Desactivar "${item.nombre_estandar}"? Quedará oculto pero se conserva el historial.`,
    )) return;
    try {
      const url = `${API_BASE}/admin/catalogo-estudios/${item.id}${hard ? "?hard=true" : ""}`;
      const r = await fetch(url, { method: "DELETE", headers: authHeaders() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail || `Error ${r.status}`);
      }
      setSnack({ severity: "success", msg: hard ? "Eliminado permanentemente" : "Desactivado" });
      cargar();
    } catch (e) {
      setSnack({ severity: "error", msg: e.message });
    }
  }

  async function reactivar(item) {
    try {
      const r = await fetch(`${API_BASE}/admin/catalogo-estudios/${item.id}/reactivar`, {
        method: "POST", headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      setSnack({ severity: "success", msg: "Reactivado" });
      cargar();
    } catch (e) {
      setSnack({ severity: "error", msg: e.message });
    }
  }

  async function guardar(payload, modo, id) {
    try {
      const url = modo === "crear"
        ? `${API_BASE}/admin/catalogo-estudios`
        : `${API_BASE}/admin/catalogo-estudios/${id}`;
      const method = modo === "crear" ? "POST" : "PUT";
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data?.detail === "string" ? data.detail : `Error ${r.status}`);
      setSnack({ severity: "success", msg: modo === "crear" ? "Estudio creado" : "Estudio actualizado" });
      setDialog(null);
      cargar();
    } catch (e) {
      setSnack({ severity: "error", msg: e.message });
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
          display: "grid", placeItems: "center", color: "#fff",
        }}>
          <ScienceIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
            Catálogo de estudios
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
            {total.toLocaleString()} registros · Laboratorio · Imagen · Especiales
          </Typography>
        </Box>
        <Tooltip title="Recargar"><IconButton onClick={cargar}><RefreshIcon /></IconButton></Tooltip>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialog({ modo: "crear", data: { nombre_estandar: "", categoria: "laboratorio", subcategoria: "", activo: true } })}
          sx={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
            textTransform: "none", fontWeight: 700,
          }}
        >
          Agregar
        </Button>
      </Box>

      {/* Tabs por categoría con conteo */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={filtroCat || "_all"}
          onChange={(_, v) => setFiltroCat(v === "_all" ? "" : v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600, fontSize: 13 } }}
        >
          <Tab
            value="_all"
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                Todos
                <Chip size="small" label={counts._total ?? 0} sx={{ height: 20, fontSize: 11 }} />
              </Box>
            }
          />
          {categorias.map((c) => (
            <Tab
              key={c}
              value={c}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  {CATEGORIA_META[c]?.icon}
                  {CATEGORIA_META[c]?.label || c}
                  <Chip
                    size="small"
                    label={counts[c] ?? 0}
                    sx={{
                      height: 20, fontSize: 11,
                      bgcolor: filtroCat === c ? `${CATEGORIA_META[c]?.color}22` : undefined,
                      color: filtroCat === c ? CATEGORIA_META[c]?.color : undefined,
                    }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Filtros */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Buscar por código, nombre o subcategoría..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 240 }}
        />
        <Button
          size="small"
          variant={mostrarInactivos ? "contained" : "outlined"}
          onClick={() => setMostrarInactivos((v) => !v)}
          sx={{ textTransform: "none" }}
        >
          {mostrarInactivos ? "Ocultar inactivos" : "Mostrar inactivos"}
        </Button>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Por página</InputLabel>
          <Select
            value={pageSize}
            label="Por página"
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : items.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#64748b" }}>
          <Typography>Sin resultados.</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "grid", gap: 1 }}>
            <Box sx={{
              gridTemplateColumns: { xs: "1fr", md: "100px 1fr 130px 140px 100px 110px" },
              gap: 1, px: 1.5, py: 0.5,
              color: "#64748b", fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
              textTransform: "uppercase",
              display: { xs: "none", md: "grid" },
            }}>
              <span>Código</span>
              <span>Nombre estándar</span>
              <span>Categoría</span>
              <span>Subcategoría</span>
              <span>Estado</span>
              <span style={{ textAlign: "right" }}>Acciones</span>
            </Box>
            {items.map((it) => (
              <Row
                key={it.id}
                item={it}
                term={debouncedQ}
                onEdit={() => setDialog({ modo: "editar", data: it })}
                onNombres={() => setNombresDialog({ estudio: it })}
                onSoftDelete={() => eliminar(it, false)}
                onHardDelete={() => eliminar(it, true)}
                onReactivar={() => reactivar(it)}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3, gap: 2, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
              Mostrando <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> de <strong>{total.toLocaleString()}</strong>
            </Typography>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                showFirstButton
                showLastButton
              />
            )}
          </Box>
        </>
      )}

      {dialog && (
        <EstudioDialog
          modo={dialog.modo}
          data={dialog.data}
          onClose={() => setDialog(null)}
          onSave={(payload) => guardar(payload, dialog.modo, dialog.data.id)}
        />
      )}

      {nombresDialog && (
        <NombresLocalesDialog
          estudio={nombresDialog.estudio}
          onClose={() => setNombresDialog(null)}
          notify={(s, m) => setSnack({ severity: s, msg: m })}
        />
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

function Row({ item, term, onEdit, onNombres, onSoftDelete, onHardDelete, onReactivar }) {
  const meta = CATEGORIA_META[item.categoria] || { label: item.categoria, color: "#94a3b8", icon: null };
  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "100px 1fr 130px 140px 100px 110px" },
      gap: 1, alignItems: "center",
      p: 1.2, borderRadius: 1.8, bgcolor: "#fff",
      border: "1px solid #e2e8f0",
      opacity: item.activo ? 1 : 0.55,
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e3a5f", fontFamily: "monospace" }}>
        <HighlightText text={item.codigo} term={term} />
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: "#0f172a" }}>
        <HighlightText text={item.nombre_estandar} term={term} />
      </Typography>
      <Chip
        size="small" icon={meta.icon} label={meta.label}
        sx={{ bgcolor: `${meta.color}15`, color: meta.color, fontWeight: 600, justifySelf: "start" }}
      />
      <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
        {item.subcategoria ? <HighlightText text={item.subcategoria} term={term} /> : "—"}
      </Typography>
      <Chip
        size="small"
        label={item.activo ? "Activo" : "Inactivo"}
        color={item.activo ? "success" : "default"}
        variant={item.activo ? "filled" : "outlined"}
        sx={{ justifySelf: "start", height: 22 }}
      />
      <Box sx={{ display: "flex", gap: 0.4, justifyContent: { md: "flex-end" } }}>
        <Tooltip title="Nombres locales por país"><IconButton size="small" onClick={onNombres} sx={{ color: "#0ea5e9" }}><LanguageIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Editar"><IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton></Tooltip>
        {item.activo ? (
          <Tooltip title="Desactivar"><IconButton size="small" onClick={onSoftDelete} sx={{ color: "#dc2626" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        ) : (
          <>
            <Tooltip title="Reactivar"><IconButton size="small" onClick={onReactivar} sx={{ color: "#16a34a" }}><RestoreIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Eliminar permanente"><IconButton size="small" onClick={onHardDelete} sx={{ color: "#b91c1c" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

function EstudioDialog({ modo, data, onClose, onSave }) {
  const [codigo, setCodigo] = useState(data.codigo || "");
  const [nombre, setNombre] = useState(data.nombre_estandar || "");
  const [categoria, setCategoria] = useState(data.categoria || "laboratorio");
  const [subcategoria, setSubcategoria] = useState(data.subcategoria || "");
  const [cupsCode, setCupsCode] = useState(data.cups_code || "");
  const [cieCode, setCieCode] = useState(data.cie_code || "");
  const [loincCode, setLoincCode] = useState(data.loinc_code || "");
  const [activo, setActivo] = useState(data.activo !== false);
  const [busy, setBusy] = useState(false);

  // Preview del próximo código (solo modo crear, se actualiza al cambiar categoría)
  const [previewCodigo, setPreviewCodigo] = useState("");
  useEffect(() => {
    if (modo !== "crear" || !categoria) return;
    let cancelled = false;
    fetch(`${API_BASE}/admin/catalogo-estudios/_next-code?categoria=${categoria}`, {
      headers: authHeaders(),
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (!cancelled && d?.codigo) setPreviewCodigo(d.codigo); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [modo, categoria]);

  // En modo crear no requerimos codigo (se auto-genera).
  const valid = nombre.trim().length > 0 && (modo === "crear" || codigo.trim().length > 0);

  return (
    <StyledDialog
      open
      onClose={busy ? undefined : onClose}
      title={modo === "crear" ? "Nuevo estudio" : `Editar #${data.id}`}
      icon={<ScienceIcon />}
      maxWidth="sm"
    >
      <StyledDialog.Body>
        <StyledDialog.FormSection>
          {modo === "crear" ? (
            <StyledDialog.FormField label="Código (auto-generado)">
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1,
                p: 1.2, borderRadius: 1.5, bgcolor: "#eff6ff",
                border: "1px solid #bfdbfe",
              }}>
                <Chip
                  label={previewCodigo || "..."}
                  size="small"
                  sx={{ fontFamily: "monospace", fontWeight: 700, bgcolor: "#1e3a5f", color: "#fff" }}
                />
                <Typography sx={{ fontSize: 12.5, color: "#1e40af" }}>
                  Se asignará al guardar según la categoría seleccionada
                </Typography>
              </Box>
              <StyledDialog.HelperText>
                El código sigue formato L###/I###/E### según la categoría. No se puede editar manualmente.
              </StyledDialog.HelperText>
            </StyledDialog.FormField>
          ) : (
            <StyledDialog.FormField label="Código" required>
              <TextField
                fullWidth size="small"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                placeholder="Ej. L045, I012, E003"
                sx={inputStyles}
                inputProps={{ maxLength: 20 }}
              />
              <StyledDialog.HelperText>
                El código no debería cambiarse normalmente. Modificar solo si hay razón clara.
              </StyledDialog.HelperText>
            </StyledDialog.FormField>
          )}

          <StyledDialog.FormField label="Nombre estándar" required>
            <TextField
              fullWidth size="small"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. Biometría hemática completa"
              sx={inputStyles}
              inputProps={{ maxLength: 200 }}
            />
          </StyledDialog.FormField>

          <StyledDialog.FormField label="Categoría">
            <FormControl fullWidth size="small">
              <Select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                <MenuItem value="laboratorio">Laboratorio</MenuItem>
                <MenuItem value="imagen">Imagen</MenuItem>
                <MenuItem value="especial">Especial</MenuItem>
              </Select>
            </FormControl>
          </StyledDialog.FormField>

          <StyledDialog.FormField label="Subcategoría (opcional)">
            <TextField
              fullWidth size="small"
              value={subcategoria}
              onChange={(e) => setSubcategoria(e.target.value)}
              placeholder="Ej. hematología, química, cardiología"
              sx={inputStyles}
              inputProps={{ maxLength: 50 }}
            />
          </StyledDialog.FormField>

          {/* Códigos estándar internacionales (opcionales) */}
          <Box sx={{ mt: 1, pt: 2, borderTop: "1px dashed #cbd5e1" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.6, mb: 1 }}>
              CÓDIGOS ESTÁNDAR (OPCIONALES)
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1.5 }}>
              <StyledDialog.FormField label="CUPS (Colombia)">
                <TextField
                  fullWidth size="small" sx={inputStyles}
                  value={cupsCode}
                  onChange={(e) => setCupsCode(e.target.value)}
                  placeholder="Ej. 902210"
                  inputProps={{ maxLength: 20 }}
                />
              </StyledDialog.FormField>
              <StyledDialog.FormField label="CIE / CMS (México)">
                <TextField
                  fullWidth size="small" sx={inputStyles}
                  value={cieCode}
                  onChange={(e) => setCieCode(e.target.value)}
                  placeholder="Ej. 90.5"
                  inputProps={{ maxLength: 20 }}
                />
              </StyledDialog.FormField>
              <StyledDialog.FormField label="LOINC (internacional)">
                <TextField
                  fullWidth size="small" sx={inputStyles}
                  value={loincCode}
                  onChange={(e) => setLoincCode(e.target.value)}
                  placeholder="Ej. 4548-4"
                  inputProps={{ maxLength: 20 }}
                />
              </StyledDialog.FormField>
            </Box>
          </Box>

          {modo === "editar" && (
            <StyledDialog.FormField label="Estado">
              <Button
                variant={activo ? "contained" : "outlined"}
                color={activo ? "success" : "warning"}
                size="small"
                onClick={() => setActivo((v) => !v)}
                sx={{ textTransform: "none" }}
              >
                {activo ? "Activo" : "Inactivo"}
              </Button>
            </StyledDialog.FormField>
          )}
        </StyledDialog.FormSection>
      </StyledDialog.Body>
      <StyledDialog.Actions>
        <StyledDialog.CancelButton onClick={onClose} disabled={busy} />
        <StyledDialog.PrimaryButton
          loading={busy}
          disabled={!valid || busy}
          onClick={async () => {
            setBusy(true);
            const payload = {
              nombre_estandar: nombre.trim(),
              categoria,
              subcategoria: subcategoria.trim() || null,
              cups_code: cupsCode.trim() || null,
              cie_code: cieCode.trim() || null,
              loinc_code: loincCode.trim() || null,
              // En modo crear, no enviamos código → el backend lo auto-genera.
              // En modo editar, sí lo enviamos para permitir cambios manuales.
              ...(modo === "editar"
                ? { codigo: codigo.trim().toUpperCase(), activo }
                : { activo: true }),
            };
            await onSave(payload);
            setBusy(false);
          }}
        >
          {modo === "crear" ? "Crear" : "Guardar"}
        </StyledDialog.PrimaryButton>
      </StyledDialog.Actions>
    </StyledDialog>
  );
}


function NombresLocalesDialog({ estudio, onClose, notify }) {
  const [items, setItems] = useState([]);
  const [paisesSoportados, setPaisesSoportados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pais, setPais] = useState("");
  const [nombre, setNombre] = useState("");

  async function cargar() {
    setLoading(true);
    try {
      const [rNombres, rPaises] = await Promise.all([
        fetch(`${API_BASE}/admin/catalogo-estudios/${estudio.id}/nombres`, { headers: authHeaders() }),
        fetch(`${API_BASE}/admin/catalogo-estudios/_paises`, { headers: authHeaders() }),
      ]);
      if (!rNombres.ok) throw new Error(`Error ${rNombres.status}`);
      setItems(await rNombres.json());
      if (rPaises.ok) setPaisesSoportados(await rPaises.json());
    } catch (e) {
      notify("error", e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  // Países que aún no tienen nombre local en este estudio
  const paisesDisponibles = useMemo(() => {
    const usados = new Set(items.map((i) => i.pais));
    return paisesSoportados.filter((p) => !usados.has(p.code));
  }, [paisesSoportados, items]);

  async function agregar() {
    const p = pais.trim().toUpperCase();
    const n = nombre.trim();
    if (!p || !n) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/admin/catalogo-estudios/${estudio.id}/nombres`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ pais: p, nombre_local: n }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data?.detail === "string" ? data.detail : `Error ${r.status}`);
      setPais("");
      setNombre("");
      cargar();
      notify("success", "Nombre local agregado");
    } catch (e) {
      notify("error", e.message);
    } finally {
      setBusy(false);
    }
  }

  async function eliminar(item) {
    if (!window.confirm(`¿Eliminar "${item.nombre_local}" (${item.pais})?`)) return;
    try {
      const r = await fetch(
        `${API_BASE}/admin/catalogo-estudios/${estudio.id}/nombres/${item.id}`,
        { method: "DELETE", headers: authHeaders() },
      );
      if (!r.ok) throw new Error(`Error ${r.status}`);
      cargar();
      notify("success", "Nombre local eliminado");
    } catch (e) {
      notify("error", e.message);
    }
  }

  return (
    <StyledDialog
      open
      onClose={busy ? undefined : onClose}
      title="Nombres locales por país"
      subtitle={`${estudio.codigo} · ${estudio.nombre_estandar}`}
      icon={<LanguageIcon />}
      maxWidth="sm"
    >
      <StyledDialog.Body>
        <StyledDialog.InfoBox title="Cómo funciona">
          El nombre estándar global es <strong>"{estudio.nombre_estandar}"</strong>. Aquí
          puedes agregar variantes alternativas que se usan en países específicos
          (ej. <em>"Hemograma"</em> en CO o <em>"BHC"</em> en MX). Cuando un médico de
          ese país busque el estudio, verá su nombre local.
        </StyledDialog.InfoBox>

        {/* Lista actual */}
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.6, mb: 1 }}>
            NOMBRES REGISTRADOS
          </Typography>
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 3, color: "#94a3b8", fontSize: 13 }}>
              Aún no hay nombres locales. Agrega uno abajo.
            </Box>
          ) : (
            <Box sx={{ display: "grid", gap: 0.6 }}>
              {items.map((it) => {
                const meta = paisesSoportados.find((p) => p.code === it.pais);
                return (
                  <Box key={it.id} sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    p: 1.2, borderRadius: 1.5, bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}>
                    <Chip
                      size="small"
                      label={meta ? `${meta.flag} ${meta.code}` : it.pais}
                      sx={{ height: 22, fontWeight: 700 }}
                    />
                    <Typography sx={{ flex: 1, fontSize: 13.5 }}>{it.nombre_local}</Typography>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => eliminar(it)} sx={{ color: "#dc2626" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Agregar nuevo */}
        <Box sx={{ mt: 3, pt: 2, borderTop: "1px dashed #cbd5e1" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 0.6, mb: 1 }}>
            AGREGAR NUEVO
          </Typography>
          {paisesDisponibles.length === 0 && !loading ? (
            <Alert severity="info" sx={{ fontSize: 13 }}>
              Ya tienes nombres locales para todos los países soportados.
            </Alert>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "180px 1fr auto", gap: 1, alignItems: "start" }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>País</InputLabel>
                <Select
                  value={pais}
                  label="País"
                  onChange={(e) => setPais(e.target.value)}
                >
                  {paisesDisponibles.map((p) => (
                    <MenuItem key={p.code} value={p.code}>
                      <Box component="span" sx={{ mr: 1 }}>{p.flag}</Box>
                      {p.label} ({p.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small" sx={inputStyles}
                label="Nombre local"
                placeholder="Ej. Hemograma"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                inputProps={{ maxLength: 200 }}
                onKeyDown={(e) => { if (e.key === "Enter") agregar(); }}
              />
              <Button
                variant="contained"
                disabled={busy || !pais || !nombre.trim()}
                onClick={agregar}
                sx={{
                  background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                  textTransform: "none", fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                Agregar
              </Button>
            </Box>
          )}
        </Box>
      </StyledDialog.Body>
      <StyledDialog.Actions>
        <StyledDialog.PrimaryButton onClick={onClose}>Cerrar</StyledDialog.PrimaryButton>
      </StyledDialog.Actions>
    </StyledDialog>
  );
}
